/**
 * useStudentTodoReorder — l'élève range sa liste à faire, et le serveur l'enregistre.
 *
 * Le geste du legacy (`TodoListScreen.js:1183-1197`, `onDragEnd`) : la liste prend l'ordre posé tout
 * de suite (`setTodoActivities`), puis `PUT api/v1/activities/todo/indexes`
 * (`activitiesService.activityTodoIndexesAsStudent`, tous les identifiants dans l'ordre), dont la
 * réponse range la liste servie (réducteur `currentUser`). Deux choses de plus que le legacy :
 *  - un échec **remet la liste à sa place** — l'ordre que le serveur tient — et le dit en bas de
 *    l'écran, sans boîte (`offlineDialog: false`). Le legacy gardait l'ordre refusé à l'écran,
 *    jusqu'à la relecture suivante qui le faisait sauter ; hors ligne, il ouvrait sa boîte ;
 *  - deux rangements rapides ne se marchent pas dessus : la réponse du premier, arrivée après le
 *    second, ne fait pas revenir la liste en arrière.
 *
 * Un refus du serveur (pas un trou réseau) veut dire que la liste a changé de son côté — le masteur
 * a ajouté une activité : `onRejected` relit le compte.
 *
 * Le séquencement est une fonction pure (`todoReorderStep`), testée en rejouant des suites
 * d'événements ; ce crochet ne fait que brancher le magasin et l'appel.
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch, useStore } from "react-redux"

import { setTodoActivities } from "../../../../../../data/redux/actions/data"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { apiCall, apiFailureOf } from "../../../../../../engine/shared/apiCall"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import {
    reorderTodoActivities,
    todoActivityIds,
} from "../../../../adapters/student/Activities/studentActivitiesAdapter"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

export type TodoReorderFailure = "offline" | "rejected"

export type TodoReorderState = {
    /** Le numéro du dernier envoi ; `0` avant le premier. */
    latest: number
    /** Le dernier envoi est en route. */
    saving: boolean
    /** L'ordre que le serveur tient : la liste d'avant le premier envoi en route, puis chaque réponse. */
    confirmed: ApiActivity[] | null
    /** Le dernier ordre posé par l'élève, pas encore confirmé. */
    optimistic: ApiActivity[] | null
    /** Le dernier envoi a échoué, la liste est revenue à `confirmed`. */
    failure: TodoReorderFailure | null
}

export type TodoReorderEvent =
    | { type: "REORDERED"; before: ApiActivity[]; next: ApiActivity[] }
    | { type: "SAVED"; seq: number; list: ApiActivity[] }
    | { type: "FAILED"; seq: number; failure: TodoReorderFailure }
    | { type: "DISMISSED" }

export type TodoReorderStep = {
    state: TodoReorderState
    /** La liste à poser dans le compte (`setTodoActivities`), ou rien. */
    write: ApiActivity[] | null
    /** L'envoi à faire, ou rien. */
    send: { seq: number; ids: (string | number)[] } | null
}

export const initialTodoReorderState: TodoReorderState = {
    latest: 0,
    saving: false,
    confirmed: null,
    optimistic: null,
    failure: null,
}

export function todoReorderStep(state: TodoReorderState, event: TodoReorderEvent): TodoReorderStep {
    switch (event.type) {
        case "REORDERED": {
            const seq = state.latest + 1
            return {
                // Un envoi déjà en route : l'ordre confirmé reste celui d'avant lui, pas son ordre posé.
                state: {
                    latest: seq,
                    saving: true,
                    confirmed: state.saving ? state.confirmed : event.before,
                    optimistic: event.next,
                    failure: null,
                },
                write: event.next,
                send: { seq, ids: todoActivityIds(event.next) },
            }
        }
        case "SAVED": {
            if (event.seq === state.latest) {
                // Le réducteur a rangé la réponse : c'est elle, la liste.
                return {
                    state: { ...state, saving: false, confirmed: event.list, optimistic: null },
                    write: null,
                    send: null,
                }
            }
            // La réponse d'un envoi dépassé : le réducteur vient de ranger un ordre plus ancien que
            // celui que l'élève voit. Le dernier ordre posé se remet par-dessus.
            return { state: { ...state, confirmed: event.list }, write: state.optimistic, send: null }
        }
        case "FAILED": {
            // L'échec d'un envoi dépassé ne remet rien : le suivant porte la liste entière.
            if (event.seq !== state.latest || !state.saving) return { state, write: null, send: null }
            return {
                state: { ...state, saving: false, optimistic: null, failure: event.failure },
                write: state.confirmed,
                send: null,
            }
        }
        case "DISMISSED":
            return { state: state.failure == null ? state : { ...state, failure: null }, write: null, send: null }
        default:
            return { state, write: null, send: null }
    }
}

const phaseOf = (state: TodoReorderState) => (state.saving ? "saving" : state.failure != null ? "failed" : "idle")

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const todoActivitiesOf = (state: any): ApiActivity[] => selectDisplayedStudent(state)?.todo_activities ?? []

export function useStudentTodoReorder(onRejected?: () => void) {
    const dispatch = useDispatch()
    const store = useStore()
    const telemetry = useMemo(() => createTelemetry("activities-student.reorder", { role: "student" }), [])
    const stateRef = useRef(initialTodoReorderState)
    const [failure, setFailure] = useState<TodoReorderFailure | null>(null)
    const onRejectedRef = useRef(onRejected)
    onRejectedRef.current = onRejected

    const run = useCallback(
        (event: TodoReorderEvent) => {
            const before = stateRef.current
            const { state, write, send } = todoReorderStep(before, event)
            stateRef.current = state
            if (phaseOf(before) !== phaseOf(state)) telemetry.transition(phaseOf(before), phaseOf(state), event.type)
            if (write != null) dispatch(setTodoActivities(write) as never)
            setFailure(state.failure)
            if (event.type === "FAILED" && before.saving && state.failure === "rejected") {
                onRejectedRef.current?.()
            }
            if (send == null) return

            apiCall<ApiActivity[]>(dispatch as never, activitiesService.activityTodoIndexesAsStudent, {
                data: { todo_activities_ids: send.ids },
            })
                .then((list) => run({ type: "SAVED", seq: send.seq, list: Array.isArray(list) ? list : [] }))
                .catch((cause: unknown) => {
                    const offline = apiFailureOf(cause)?.isOffline === true
                    // Un trou réseau est attendu ; un refus du serveur ne l'est pas, il part dans Bugsnag.
                    if (!offline) telemetry.error("save", cause, { count: send.ids.length })
                    run({ type: "FAILED", seq: send.seq, failure: offline ? "offline" : "rejected" })
                })
        },
        [dispatch, telemetry]
    )

    const reorder = useCallback(
        (ids: (string | number)[]) => {
            const before = todoActivitiesOf(store.getState())
            const next = reorderTodoActivities(before, ids)
            // L'ordre ne décrit plus la liste (une relecture est passée sous le doigt) : rien ne part.
            if (next == null) {
                telemetry.transition(phaseOf(stateRef.current), phaseOf(stateRef.current), "STALE_ORDER")
                return
            }
            run({ type: "REORDERED", before, next })
        },
        [run, store, telemetry]
    )

    const dismiss = useCallback(() => run({ type: "DISMISSED" }), [run])

    return { failure, reorder, dismiss }
}
